'use client';

import * as React from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { UnsafeProps } from '../escape-hatches';
import { cx } from '../utils';
import { devWarn } from '../internal/devWarn';

/* ─────────────────────────────────────────────────────
   Reduced-motion helper. Reads matchMedia synchronously
   at call time (NOT cached) so subscribers to system
   preference changes pick up the live value at the next
   value-change scroll. Defensive over `window` and
   `matchMedia` for SSR / non-browser environments.
   ───────────────────────────────────────────────────── */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/* ═══════════════════════════════════════════════════
   SegmentedControl — single-select segmented tray with a
   sliding active pill. Ships in two shapes per ADR-033:

     1. Convenience `options` form (legacy / simple cases)
          <SegmentedControl options={[{value,label,icon?}]} ... />
        The `options` callsite wraps the same internal Root/Item
        engine via a thin adapter — one behavior implementation
        for both shapes (ADR-033 §2 carrier table).

     2. Compound `Root` / `Item` form (rich children)
          <SegmentedControl.Root value onValueChange ariaLabel>
            <SegmentedControl.Item value="a">A</SegmentedControl.Item>
            <SegmentedControl.Item value="b">
              <span>B</span><Badge>12</Badge>
            </SegmentedControl.Item>
          </SegmentedControl.Root>

   APG: pressed-button group (role="group" + <button aria-pressed>),
   NOT radiogroup — preserved per ADR-033 §3. Full keyboard
   coverage (roving tabindex, Home/End, disabled-skip) lives in
   the kit, not in adopter code (ADR-033 §3).
   ═══════════════════════════════════════════════════ */

export interface SegmentedOption<V extends string = string> {
  value: V;
  label: ReactNode;
  icon?: ReactNode;
  /** Per-option disabled. Adds to the deferred-list closure noted in
   * docs/DEFERRED.md and ADR-033 §2. */
  disabled?: boolean;
}

export type SegmentedSize = 'sm' | 'md';

/* ── Internal context (compound mode) ─────────────── */

interface SegmentedContextValue {
  value: string;
  size: SegmentedSize;
  groupDisabled: boolean;
  focusedValue: string | null;
  onItemSelect: (itemValue: string) => void;
  onFocusItem: (itemValue: string) => void;
}

const SegmentedContext = React.createContext<SegmentedContextValue | null>(null);

/* ── Root ─────────────────────────────────────────── */

export interface SegmentedControlRootProps<V extends string = string> extends UnsafeProps {
  value: V;
  onValueChange: (value: V) => void;
  /** Required accessible name for the group. */
  ariaLabel?: string;
  size?: SegmentedSize;
  disabled?: boolean;
  /** Sizing (#136). `auto` (default) = fit-content; `fill` = stretch to the
   * container's inline size, distributing options evenly. */
  width?: 'auto' | 'fill';
  /** Native tooltip for the group (#137). Maps to the `title` attribute on
   * the root; distinct from `ariaLabel` (the accessible name). */
  title?: string;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}

function useGroupKeyboard(
  containerRef: React.RefObject<HTMLDivElement>,
  focusedValue: string | null,
  setFocusedValue: (v: string) => void,
) {
  /** Get all option buttons in DOM order. Item buttons carry the
   * j-segmented-option class so the selector matches both the
   * compound mode and the options-prop adapter. */
  const getButtons = React.useCallback((): HTMLButtonElement[] => {
    if (!containerRef.current) return [];
    return Array.from(
      containerRef.current.querySelectorAll<HTMLButtonElement>('button.j-segmented-option'),
    );
  }, [containerRef]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(e.key)) return;
      e.preventDefault();
      const buttons = getButtons();
      const enabled = buttons.filter((b) => !b.disabled);
      if (enabled.length === 0) return;

      const currentBtn = buttons.find((b) => b.dataset.value === focusedValue);
      const currentIdx = currentBtn ? enabled.indexOf(currentBtn) : -1;

      let nextBtn: HTMLButtonElement;
      switch (e.key) {
        case 'ArrowRight':
          nextBtn =
            currentIdx === -1 || currentIdx >= enabled.length - 1
              ? enabled[0]
              : enabled[currentIdx + 1];
          break;
        case 'ArrowLeft':
          nextBtn = currentIdx <= 0 ? enabled[enabled.length - 1] : enabled[currentIdx - 1];
          break;
        case 'Home':
          nextBtn = enabled[0];
          break;
        case 'End':
          nextBtn = enabled[enabled.length - 1];
          break;
        default:
          return;
      }
      const nextVal = nextBtn.dataset.value;
      if (nextVal != null) setFocusedValue(nextVal);
    },
    [focusedValue, getButtons, setFocusedValue],
  );

  return { handleKeyDown, getButtons };
}

function SegmentedRoot<V extends string = string>(
  props: SegmentedControlRootProps<V>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const {
    value,
    onValueChange,
    ariaLabel,
    size = 'md',
    disabled = false,
    width = 'auto',
    title,
    className,
    style,
    unsafe_className,
    unsafe_style,
    children,
  } = props;

  /* Per-instance dedupe — see legacy implementation note. */
  const warnedRef = React.useRef(false);
  React.useEffect(() => {
    if (ariaLabel || warnedRef.current) return;
    warnedRef.current = true;
    devWarn(
      '[JKit.SegmentedControl] rendered without `ariaLabel`; the group ' +
        'has no accessible name and assistive tech will announce it as ' +
        '"group" only. Pass an `ariaLabel` describing what the control selects.',
    );
  }, [ariaLabel]);

  const containerRef = React.useRef<HTMLDivElement>(null);
  const setRef = (node: HTMLDivElement | null) => {
    (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
  };

  /* Per-item disabled state lives on the rendered DOM nodes; the keyboard
     handler queries the DOM directly via getButtons() and filters by the
     button's `disabled` attribute. Mirrors the Toggle/ToggleGroup
     pattern — no per-item registry needed because the keyboard engine
     never needs a snapshot independent of layout. */

  const [focusedValue, setFocusedValue] = React.useState<string | null>(() => value);
  /* Keep the roving-focus marker aligned with the controlled value when
     the consumer drives `value` externally. */
  React.useEffect(() => {
    setFocusedValue(value);
  }, [value]);

  const onItemSelect = React.useCallback(
    (itemValue: string) => {
      (onValueChange as (v: string) => void)(itemValue);
    },
    [onValueChange],
  );

  const onFocusItem = React.useCallback((itemValue: string) => {
    setFocusedValue(itemValue);
  }, []);

  const { handleKeyDown } = useGroupKeyboard(containerRef, focusedValue, setFocusedValue);

  /* ── Sliding pill measurement ──
     Same anatomy as legacy: read the active option's offsetLeft /
     offsetWidth and expose --_seg-x / --_seg-w. Re-measure on value
     change and container resize (e.g., font swap or layout reflow). */
  const mountedRef = React.useRef(false);
  const animationTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const measure = React.useCallback(() => {
    const track = containerRef.current;
    if (!track) return;
    const activeBtn = track.querySelector('[data-active="true"]') as HTMLElement | null;
    if (!activeBtn) return;
    track.style.setProperty('--_seg-x', `${activeBtn.offsetLeft}px`);
    track.style.setProperty('--_seg-w', `${activeBtn.offsetWidth}px`);
  }, []);

  const setHoverUnderlay = React.useCallback((option: HTMLElement | null, visible: boolean) => {
    const track = containerRef.current;
    if (!track || !option || !visible) {
      containerRef.current?.style.setProperty('--_seg-hover-opacity', '0');
      return;
    }
    track.style.setProperty('--_seg-hover-x', `${option.offsetLeft}px`);
    track.style.setProperty('--_seg-hover-w', `${option.offsetWidth}px`);
    track.style.setProperty('--_seg-hover-opacity', '1');
  }, []);

  /* ── Animation gate (ADR-044 animation pass) ──
     The pill's CSS transition is OFF by default and only enabled while
     `data-seg-animate='true'` is on the track (see kit.css). We flip that
     attribute imperatively in the SAME synchronous effect body that triggers
     measure(), so the gate and the new --_seg-x/--_seg-w land in one paint:
       - genuine user value change → animate=true  (the 200ms decelerate slide)
       - initial mount + any ResizeObserver re-measure (font-swap, reflow,
         panel resize) → animate=false → the pill TELEPORTS, never lunges.
     This is the fix for the "flicky/jumpy" mount-flash + resize-drift. */
  const applyIndicator = React.useCallback(
    (animate: boolean) => {
      const track = containerRef.current;
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current);
        animationTimerRef.current = null;
      }
      if (track) {
        track.setAttribute('data-seg-animate', animate ? 'true' : 'false');
      }
      measure();
      if (animate) {
        animationTimerRef.current = setTimeout(() => {
          containerRef.current?.setAttribute('data-seg-animate', 'false');
          animationTimerRef.current = null;
          const hovered = containerRef.current?.querySelector(
            '.j-segmented-option[data-seg-hover="true"]',
          ) as HTMLElement | null;
          if (!hovered || hovered.dataset.active === 'true') {
            setHoverUnderlay(null, false);
          } else {
            setHoverUnderlay(hovered, true);
          }
        }, 220);
      }
    },
    [measure, setHoverUnderlay],
  );

  React.useLayoutEffect(() => {
    /* Mount = instant (place the pill on the active segment with no animate-in);
       every subsequent value change = the user-driven slide. */
    applyIndicator(mountedRef.current);
    mountedRef.current = true;
  }, [value, applyIndicator]);

  React.useEffect(
    () => () => {
      if (animationTimerRef.current) clearTimeout(animationTimerRef.current);
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    },
    [],
  );

  const clearHoverUnderlay = React.useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    containerRef.current
      ?.querySelectorAll('.j-segmented-option[data-seg-hover="true"]')
      .forEach((item) => item.removeAttribute('data-seg-hover'));
    setHoverUnderlay(null, false);
  }, [setHoverUnderlay]);

  const handlePointerOver = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const option = (event.target as HTMLElement).closest<HTMLButtonElement>(
        '.j-segmented-option',
      );
      if (!option || option.disabled || option.dataset.active === 'true') return;
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
      containerRef.current
        ?.querySelectorAll('.j-segmented-option[data-seg-hover="true"]')
        .forEach((item) => item.removeAttribute('data-seg-hover'));
      option.setAttribute('data-seg-hover', 'true');
      setHoverUnderlay(option, true);
    },
    [setHoverUnderlay],
  );

  const handlePointerLeave = React.useCallback(() => {
    const track = containerRef.current;
    track
      ?.querySelectorAll('.j-segmented-option[data-seg-hover="true"]')
      .forEach((item) => item.removeAttribute('data-seg-hover'));
    if (track?.getAttribute('data-seg-animate') === 'true') return;
    setHoverUnderlay(null, false);
  }, [setHoverUnderlay]);

  const handlePointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const option = (event.target as HTMLElement).closest<HTMLButtonElement>(
        '.j-segmented-option',
      );
      if (!option || option.disabled || option.dataset.active === 'true') return;
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
      option.setAttribute('data-seg-hover', 'true');
      setHoverUnderlay(option, true);
      hoverTimerRef.current = setTimeout(() => {
        if (containerRef.current?.getAttribute('data-seg-animate') !== 'true') {
          clearHoverUnderlay();
        }
        hoverTimerRef.current = null;
      }, 220);
    },
    [clearHoverUnderlay, setHoverUnderlay],
  );

  /* ── Narrow-container auto-scroll (v5.2.1 hotfix) ──
     When the tray overflows horizontally (narrow parent triggers the
     scroll-snap fallback declared in kit.css), center the active
     option in the tray's scrollport on mount and on every value
     change. Skip when the tray is wider than its content (no
     overflow) so wide layouts stay perfectly still.

     Scope-locked to the tray (Codex review on PR #198 round 2): the
     previous revision called `activeBtn.scrollIntoView({inline:
     'center', block: 'nearest'})`. Even with `block: 'nearest'`,
     scrollIntoView walks the ancestor chain and will scroll the page
     (or any intermediate scroll container) vertically when the
     control is offscreen. The bug we're fixing only needs horizontal
     movement INSIDE the tray, so compute the target scrollLeft from
     the active button's offset and write it directly via
     `track.scrollTo` — no ancestor scrolling, no page jump on mount.

     `inline: 'center'` semantics are preserved by centering the
     active button's center against the tray's clientWidth midpoint
     and clamping to [0, scrollWidth - clientWidth]. Reduced-motion
     users get `behavior: 'auto'` (instant); everyone else gets the
     smooth slide that matches the existing pill transition.

     Resize-driven re-scroll (Codex review on PR #198 round 1): the
     same path runs when the tray's box changes size. If the control
     mounts in a wide container and the parent later narrows (window
     resize, side-panel collapse, responsive breakpoint flip), the
     active item can be clipped off the right edge without a value
     change — re-scroll on every observed resize so the active item
     stays in the visible window. */
  const scrollActiveIntoView = React.useCallback(() => {
    const track = containerRef.current;
    if (!track) return;
    if (track.scrollWidth <= track.clientWidth) return;
    const activeBtn = track.querySelector('[data-active="true"]') as HTMLElement | null;
    if (!activeBtn) return;
    /* Center the active button inside the tray's scrollport. offsetLeft
       is measured relative to the tray (the nearest positioned
       ancestor in this layout), so the arithmetic stays correct under
       horizontal scroll. Clamp to the valid scroll range so we don't
       request a position past the right edge (which most browsers
       silently clamp anyway, but explicit is better than implicit).

       RTL note (Codex review on PR #198 round 3): in `dir="rtl"`,
       `scrollLeft` semantics vary across browsers (negative in spec,
       0..-scrollWidth in some Blink/WebKit versions, scrollWidth..0
       in older ones), and `offsetLeft` is still LTR physical. This
       computation targets the LTR case the v5.2.1 hotfix was reported
       against; a proper RTL refactor that normalizes scrollLeft via
       `getBoundingClientRect` deltas or a feature-detection table is
       deferred to a follow-up. The bug bound to this hotfix (pill
       pinned to row 1 / active button on row 2 at 360px LTR) is
       resolved either way — RTL adopters with overflowing segmented
       trays will continue to see the previous v5.1 behavior (no
       auto-scroll), which is not a regression from this PR. */
    const targetLeft = activeBtn.offsetLeft + activeBtn.offsetWidth / 2 - track.clientWidth / 2;
    const maxLeft = Math.max(0, track.scrollWidth - track.clientWidth);
    const clampedLeft = Math.max(0, Math.min(targetLeft, maxLeft));
    if (typeof track.scrollTo === 'function') {
      track.scrollTo({
        left: clampedLeft,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    } else {
      /* Defensive fallback for non-DOM environments / older test
         doubles that don't implement scrollTo. The same write via the
         scrollLeft property is synchronous and unanimated, which is
         fine since the fallback path is the reduced-motion equivalent. */
      track.scrollLeft = clampedLeft;
    }
  }, []);

  React.useEffect(() => {
    scrollActiveIntoView();
  }, [value, scrollActiveIntoView]);

  React.useEffect(() => {
    const track = containerRef.current;
    if (!track || typeof ResizeObserver === 'undefined') return;
    /* Single ResizeObserver for both jobs (was two): re-measure the pill
       (INSTANT — resize/font-swap/reflow must teleport, never slide) and
       re-center the active option in the narrow-overflow scrollport. */
    const ro = new ResizeObserver(() => {
      applyIndicator(false);
      scrollActiveIntoView();
    });
    ro.observe(track);
    return () => ro.disconnect();
  }, [applyIndicator, scrollActiveIntoView]);

  const ctx = React.useMemo<SegmentedContextValue>(
    () => ({
      value: value as string,
      size,
      groupDisabled: disabled,
      focusedValue,
      onItemSelect,
      onFocusItem,
    }),
    [value, size, disabled, focusedValue, onItemSelect, onFocusItem],
  );

  return (
    <SegmentedContext.Provider value={ctx}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- pointer handlers only drive the visual hover underlay; keyboard interaction remains on the existing roving-focus handler. */}
      <div
        ref={setRef}
        className={cx(
          'j-segmented',
          size === 'sm' && 'j-segmented-sm',
          width === 'fill' && 'j-segmented-fill',
          className,
          unsafe_className,
        )}
        title={title}
        style={{ ...style, ...unsafe_style }}
        role="group"
        aria-label={ariaLabel}
        aria-disabled={disabled ? 'true' : undefined}
        onKeyDown={handleKeyDown}
        onPointerOver={handlePointerOver}
        onPointerLeave={handlePointerLeave}
        onPointerDown={handlePointerDown}
      >
        {children}
      </div>
    </SegmentedContext.Provider>
  );
}

const SegmentedRootForwarded = React.forwardRef(SegmentedRoot) as <V extends string = string>(
  props: SegmentedControlRootProps<V> & { ref?: React.ForwardedRef<HTMLDivElement> },
) => React.ReactElement;

/* ── Item ─────────────────────────────────────────── */

export interface SegmentedControlItemProps extends UnsafeProps {
  value: string;
  /** Per-option disabled. Keyboard navigation skips disabled items. */
  disabled?: boolean;
  className?: string;
  children: ReactNode;
}

const SegmentedItem = React.forwardRef<HTMLButtonElement, SegmentedControlItemProps>(
  function SegmentedItem(
    { value, disabled: itemDisabled, className, unsafe_className, unsafe_style, children },
    ref,
  ) {
    const ctx = React.useContext(SegmentedContext);
    if (!ctx) {
      throw new Error('<SegmentedControl.Item> must be used within a <SegmentedControl.Root>.');
    }

    const { value: groupValue, groupDisabled, focusedValue, onItemSelect, onFocusItem } = ctx;

    const isActive = groupValue === value;
    const isDisabled = groupDisabled || !!itemDisabled;
    const isFocused = focusedValue === value;

    const btnRef = React.useRef<HTMLButtonElement>(null);
    const setBtnRef = (node: HTMLButtonElement | null) => {
      (btnRef as React.MutableRefObject<HTMLButtonElement | null>).current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
    };

    /* Move focus when the roving marker lands on this item via keyboard.
       Only steal focus if the user is already navigating WITHIN this group
       (its root already contains the active element). That guard prevents a
       focus-steal on initial mount, while still letting focus follow the
       marker onto the *currently-selected* item. The prior
       `focusedValue !== groupValue` guard skipped exactly that case, leaving
       document.activeElement stranded on the previous button when navigation
       landed on the selected item (WCAG 2.4.7 focus-visible / 2.4.3 focus-order). */
    React.useEffect(() => {
      const btn = btnRef.current;
      if (!isFocused || !btn || document.activeElement === btn) return;
      const root = btn.closest('[role="group"]');
      if (root && root.contains(document.activeElement)) {
        btn.focus();
      }
    }, [isFocused]);

    return (
      <button
        ref={setBtnRef}
        type="button"
        data-value={value}
        data-active={isActive ? 'true' : undefined}
        aria-pressed={isActive}
        disabled={isDisabled}
        tabIndex={isFocused ? 0 : -1}
        className={cx('j-segmented-option', className, unsafe_className)}
        style={unsafe_style}
        onClick={() => {
          if (isDisabled) return;
          onFocusItem(value);
          onItemSelect(value);
        }}
      >
        {children}
      </button>
    );
  },
);

/* ── Options-prop adapter (legacy convenience form) ──
   Renders the same Root + Item tree internally so the behavior engine
   is shared. Exposed via the SegmentedControl callable below. */

export interface SegmentedControlProps<V extends string = string> extends UnsafeProps {
  value: V;
  onChange: (value: V) => void;
  options: SegmentedOption<V>[];
  ariaLabel?: string;
  size?: SegmentedSize;
  disabled?: boolean;
  /** Sizing (#136) — `auto` (default) | `fill`. See SegmentedControlRootProps. */
  width?: 'auto' | 'fill';
  /** Native tooltip (#137) — maps to the root `title` attribute. */
  title?: string;
  className?: string;
  style?: CSSProperties;
}

function SegmentedControlLegacy<V extends string = string>({
  value,
  onChange,
  options,
  ariaLabel,
  size,
  disabled,
  width,
  title,
  className,
  style,
  unsafe_className,
  unsafe_style,
}: SegmentedControlProps<V>) {
  /* Forward the escape-hatch props by composing them into className/
     style at the adapter boundary. Re-emitting them as JSX attributes
     with the unsafe_* prefix on a kit primitive would trip the
     @atelierpx/jacquard/no-experimental-without-prefix lint rule at the
     call site even though the surface is internal. The composition
     here is semantically identical to passing the props through —
     cx() preserves order and the spread merges objects in place. */
  const mergedClassName = cx(className, unsafe_className);
  const mergedStyle: CSSProperties | undefined =
    style || unsafe_style ? { ...style, ...unsafe_style } : undefined;
  return (
    <SegmentedRootForwarded<V>
      value={value}
      onValueChange={onChange}
      ariaLabel={ariaLabel}
      size={size}
      disabled={disabled}
      width={width}
      title={title}
      className={mergedClassName}
      style={mergedStyle}
    >
      {options.map((o) => (
        <SegmentedItem key={o.value} value={o.value} disabled={o.disabled}>
          {o.icon}
          {o.label}
        </SegmentedItem>
      ))}
    </SegmentedRootForwarded>
  );
}

/* Callable signature kept for backward compatibility with the v5.1
   options-prop API. Adopters using the flat call form keep working
   without a codemod (ADR-033 §1 — compound is additive). New code
   should use SegmentedControl.Root / SegmentedControl.Item. */
export const SegmentedControl = Object.assign(SegmentedControlLegacy, {
  Root: SegmentedRootForwarded,
  Item: SegmentedItem,
}) as (<V extends string = string>(props: SegmentedControlProps<V>) => React.ReactElement) & {
  Root: typeof SegmentedRootForwarded;
  Item: typeof SegmentedItem;
};
